import { AfterViewInit, Directive, ElementRef, HostBinding, inject, OnDestroy } from '@angular/core';

import { CommentService } from '../comment.service';

/**
 * This directive must be applied on the whole container that holds commentable elements.
 * It adds a specific class to the host element which will be used to find the comment elements inside.
 */
@Directive({
  selector: '[ttCommentableContainer]',
  standalone: true,
})
export class CommentableContainerDirective implements AfterViewInit, OnDestroy {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private commentService = inject(CommentService, { optional: true });
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private scrollContainer?: HTMLElement;
  private refreshFrame?: number;
  private positionRefreshFrame?: number;

  @HostBinding('class.tt-commentable-container')
  public hostClass = true;

  public ngAfterViewInit(): void {
    this.mutationObserver = new MutationObserver(() => this.scheduleOrderRefresh());
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'transform'],
      childList: true,
      subtree: true,
    });
    this.resizeObserver = new ResizeObserver(() => this.scheduleOrderRefresh());
    this.resizeObserver.observe(this.elementRef.nativeElement);
    this.scrollContainer = this.elementRef.nativeElement.closest<HTMLElement>('.tt-drawer-content') ?? this.elementRef.nativeElement;
    this.scrollContainer.addEventListener('scroll', this.schedulePositionRefresh, { passive: true });
    this.scheduleOrderRefresh();
  }

  public ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.scrollContainer?.removeEventListener('scroll', this.schedulePositionRefresh);
    if (this.refreshFrame !== undefined) {
      cancelAnimationFrame(this.refreshFrame);
    }
    if (this.positionRefreshFrame !== undefined) {
      cancelAnimationFrame(this.positionRefreshFrame);
    }
  }

  private scheduleOrderRefresh(): void {
    if (!this.commentService || this.refreshFrame !== undefined) {
      return;
    }

    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = undefined;
      this.commentService?.refreshCommentOrder();
    });
  }

  private schedulePositionRefresh = (): void => {
    if (!this.commentService || this.positionRefreshFrame !== undefined) {
      return;
    }

    this.positionRefreshFrame = requestAnimationFrame(() => {
      this.positionRefreshFrame = undefined;
      this.commentService?.refreshCommentPosition();
    });
  };
}
